import { useEffect, useState, useRef } from 'react'

import type { Product, CertificateGroupDivisibility } from 'types'

import { getErrorMessage } from 'utils'
import { useSnackbar, useEncodingDevice } from 'contexts'
import { useProducts } from 'hooks/api'
import { api } from 'services'

import { Box, Collapse, Divider, Radio, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { ChevronLeft, InfoIcon, PackageIcon, PlusIcon } from 'lucide-react'

import { Alert, Button, Modal } from '@repo/ui'
import {
  DivisibilityOptionPicker,
  ProductPreview,
  SearchFilter,
  ScanSessionBlock,
  type ScanSessionBlockRef,
  PACKAGE_HANDLE_URL_RE,
} from 'components'

interface CreateCertificatesModalProps {
  product?: Product | null
  open: boolean
  onClose?: () => void
  refetch?: () => void
}

/** Inline status for the "scan a package" bulk-create affordance below. */
interface PackageScanStatus {
  loading: boolean
  message: string | null
  severity: 'info' | 'error' | 'success'
}

type CertificateMode = 'single' | 'group'

// VRG-337 (§36) — the DIVISIBILITY_OPTIONS const + card markup that used to
// live here was extracted to the shared `DivisibilityOptionPicker`
// (components/input/DivisibilityOptionPicker) on its second hand-roll, when
// "Group existing certificates" needed the exact same choice.

export const CreateCertificatesModal = (props: CreateCertificatesModalProps) => {
  const { open, onClose, refetch } = props
  const theme = useTheme()
  const [product, setProduct] = useState<Product | null>(null)
  // Was the product supplied by the opener (product page) vs picked in the modal
  // (Certificates page)? When preselected, which product is implied by context, so
  // the "Selected product" card is redundant and hidden — matching the Set-default-tag
  // modal. When picked here, the card IS the selection (with "Change"), so it stays.
  const [isPreselected, setIsPreselected] = useState<boolean>(false)
  const [scannedTagIds, setScannedTagIds] = useState<string[]>([])
  const [pendingCount, setPendingCount] = useState<number>(0)
  const [isCreatingCertificates, setIsCreatingCertificates] = useState<boolean>(false)
  const scanSessionRef = useRef<ScanSessionBlockRef>(null)

  // VRG-233 — the 2-step wizard: which step is showing, and the Single/Group choice
  // (+ divisibility, when Group) made in Step 1.
  const [activeStep, setActiveStep] = useState<number>(0)
  const [certMode, setCertMode] = useState<CertificateMode>('single')
  const [divisibility, setDivisibility] = useState<CertificateGroupDivisibility | null>(null)
  // Tags eligible to become NEW group members (ScanSessionBlock excludes already-certified
  // ones — see onGroupableTagsChange there), partitioned into groups by `groupBoundaries`:
  // each entry is the index in `groupableTagIds` where that group's tags begin. "Add Next
  // Group" appends a new boundary at the current length, closing off the group in progress.
  const [groupableTagIds, setGroupableTagIds] = useState<string[]>([])
  const [groupBoundaries, setGroupBoundaries] = useState<number[]>([0])
  const [isCreatingGroup, setIsCreatingGroup] = useState<boolean>(false)
  // VRG-233 — the phone (QR) connection, reported by ScanSessionBlock. The QR only
  // shows in Step 1 (showMethodSelector), so "Next" is gated on being connected —
  // otherwise Step 2 could render with nothing to scan and no way back to the QR
  // short of "Back".
  const [scanDeviceConnected, setScanDeviceConnected] = useState<boolean>(false)

  const { open: notification } = useSnackbar()
  // VERA-790 Phase 3 "package handle" — scan a package's handle tag to bulk-create
  // certificates for every tag it contains, instead of scanning each tag individually.
  // Single mode only (VRG-233 AC6) — a package's tags are independent items, which
  // conflicts with a group's "N tagged components of ONE item" model.
  //
  // `isConnected` is still read, but ONLY as one arm of VRG-233's step-1→step-2 gate
  // (`scanDeviceConnected || readerConnected`). Main removed it because it was GATING the
  // package row on a reader, which locked phone-only operators out of a capability the
  // phone has; that gate is gone. An OR that merely asks "is SOME scan device connected"
  // does not reintroduce the problem — a phone-only operator satisfies it via the left arm.
  const { isConnected: readerConnected, subscribeScan } = useEncodingDevice()
  const [packageScanStatus, setPackageScanStatus] = useState<PackageScanStatus>({
    loading: false,
    message: null,
    severity: 'info',
  })
  // Guards against the reader firing more than one scan event for the same tap before the
  // first resolve completes (mirrors EncodeConfigureModal's scanResolvingRef).
  const packageScanResolvingRef = useRef(false)

  // Preselect the opener's product every time the modal OPENS — keyed on `open`, not just
  // on `props.product` changing. Keying only on `props.product` meant closing + reopening
  // from the same product page didn't re-fire (the prop reference was unchanged) and the
  // modal fell back to the picker. `handleClose` resets state on close, so a later open
  // with no product (Certificates page) still starts blank.
  useEffect(() => {
    if (open && props.product?.id) {
      setProduct(props.product)
      setIsPreselected(true)
    }
  }, [open, props.product])

  //TODO: TRE, virtualized list and pull in from shopify integration
  const { data, isLoading: isLoadingProducts } = useProducts(
    { limit: 100, offset: 0 },
    { enabled: open && !product?.id }, // Only fetch when modal is open and no product is selected
  )

  const products = (data?.data as Product[]) || []

  // Read the freshest `product` / `products` from a ref inside the scan-subscription
  // callback below without re-subscribing to the reader stream on every render.
  const productRef = useRef<Product | null>(product)
  useEffect(() => { productRef.current = product }, [product])

  // The product is locked once tags have actually been scanned (pending tags are
  // tied to this product) — NOT merely on selection. Until a tag is scanned the
  // user can freely "Change" their pick. Group mode counts its own eligible tags too.
  const locked = pendingCount > 0 || groupableTagIds.length > 0

  // VRG-233 — Step 1 → Step 2 gate: a product is always required; Group mode also
  // requires a divisibility choice (AC2); either scan device must actually be
  // connected — the QR/reader-setup panels only render in Step 1, so advancing
  // before connecting would strand Step 2 with nothing to scan yet.
  const canProceedToScanning =
    Boolean(product?.id) &&
    (certMode === 'single' || Boolean(divisibility)) &&
    (scanDeviceConnected || readerConnected)

  // VRG-512 (§46) — "Next" used to disable with no explanation. A company with no phone
  // paired and no reader plugged in (the common case for anyone without physical tag
  // inventory on hand, e.g. reviewing a freshly-seeded sample company) would hit a dead
  // button with nothing telling them why. Named per the SPECIFIC unmet prerequisite,
  // checked in the same order `canProceedToScanning` checks them, so the message always
  // matches the next thing actually blocking them.
  const nextDisabledReason = !product?.id
    ? 'Select a product to continue'
    : certMode === 'group' && !divisibility
      ? 'Choose Divisible or Indivisible to continue'
      : !scanDeviceConnected && !readerConnected
        ? 'Connect a phone or hardware reader below to continue'
        : null

  // VRG-233 — current (in-progress) group's tag count + 1-based label, derived from
  // groupableTagIds/groupBoundaries. The backend requires >= 2 tagged components per
  // group (a group of one is meaningless), so both "Add Next Group" and the final
  // submit gate on that.
  const currentGroupStart = groupBoundaries[groupBoundaries.length - 1]
  const currentGroupCount = groupableTagIds.length - currentGroupStart
  const groupIndex = groupBoundaries.length

  const handleClearProduct = () => {
    if (locked) return
    setProduct(null)
    setIsPreselected(false)
    setScannedTagIds([])
    setPendingCount(0)
  }

  const handleCreateCertificates = async () => {
    if (!scanSessionRef.current || pendingCount === 0) return

    setIsCreatingCertificates(true)
    try {
      await scanSessionRef.current.createCertificates()
      refetch?.()
      // Close modal automatically on successful certificate creation
      handleClose()
    } catch (error) {
      // Surface the failure (was previously console-only) and keep the modal open
      // so the user can retry.
      notification({ type: 'error', title: getErrorMessage(error) })
    } finally {
      setIsCreatingCertificates(false)
    }
  }

  // VRG-233 — resolve groupableTagIds/groupBoundaries into the final group slices
  // (one tagUids[] per group), dropping any that never reached the 2-tag minimum
  // (shouldn't happen given the CTA/Add-Next-Group gates, but defensive).
  const resolveGroups = (): string[][] =>
    groupBoundaries
      .map((start, i) => {
        const end = groupBoundaries[i + 1] ?? groupableTagIds.length
        return groupableTagIds.slice(start, end)
      })
      .filter(group => group.length >= 2)

  const handleAddNextGroup = () => {
    if (currentGroupCount < 2) return
    setGroupBoundaries(prev => [...prev, groupableTagIds.length])
  }

  // VRG-233 — creates one certificate group per scanned set via the VRG-232 endpoint
  // (POST /certificates/admin/groups). Each group is its own request/transaction
  // (per the backend's own use case), so a failure on group 2 never orphans group 1's
  // already-created certificates (AC5) — it just isn't included in this submit's
  // success count, and the modal stays open so the user can retry it.
  const handleCreateGroups = async () => {
    if (!product?.id || !divisibility || isCreatingGroup) return
    const groups = resolveGroups()
    if (groups.length === 0) return

    setIsCreatingGroup(true)
    let createdGroups = 0
    let firstError: string | null = null
    for (const tagUids of groups) {
      try {
        const response = await api.post('/certificates/admin/groups', {
          productId: product.id,
          tagUids,
          divisibility,
        })
        const status = response?.status
        if (typeof status !== 'number' || status < 200 || status >= 300) {
          throw new Error(response?.data?.message || 'Failed to create certificate group')
        }
        createdGroups += 1
      } catch (error) {
        firstError = firstError ?? getErrorMessage(error)
      }
    }
    setIsCreatingGroup(false)

    if (createdGroups > 0) refetch?.()

    if (firstError) {
      notification({
        type: createdGroups > 0 ? 'warning' : 'error',
        title:
          createdGroups > 0
            ? `Created ${createdGroups} of ${groups.length} certificate groups`
            : 'Failed to create certificate group',
        description: firstError,
      })
      // Keep the modal open when nothing was created so the user can retry;
      // otherwise fall through and close (matches handleCreateCertificates below).
      if (createdGroups === 0) return
    } else {
      notification({
        type: 'success',
        title: `Successfully created ${createdGroups} certificate group${createdGroups === 1 ? '' : 's'}`,
      })
    }
    handleClose()
  }

  const handleClose = () => {
    setProduct(null)
    setIsPreselected(false)
    setScannedTagIds([])
    setPendingCount(0)
    setPackageScanStatus({ loading: false, message: null, severity: 'info' })
    packageScanResolvingRef.current = false
    setActiveStep(0)
    setCertMode('single')
    setDivisibility(null)
    setGroupableTagIds([])
    setGroupBoundaries([0])
    setScanDeviceConnected(false)
    onClose?.()
  }

  // Best-effort match: a package's tags all share ONE physical tag-shop SKU (the physical NFC
  // label the tags were encoded from), NOT necessarily a real merchandise Product's SKU/title —
  // those are two separate catalogs (TagShopProduct = the tag hardware; Product = the company's
  // certified item). This match only succeeds when an admin has set a merchandise Product's
  // sku/title to the SAME value as the tag-shop product used to encode the package (a real but
  // common setup in this internal tool). When it doesn't match, the operator picks the product
  // manually before scanning — the modal's normal picker still works exactly as before.

  // VERA-790 Phase 3 "package handle": listen for the reader scanning a package's handle tag
  // (the plain NDEF URL label on the box, written by EncodePackageHandleModal) while this modal
  // is open. On a match: resolve the package's tags, resolve/auto-select the product, then
  // bulk-create certificates for every tag at once via a direct backend endpoint — NOT routed
  // through ScanSessionBlock's SSE scan session, since that path decodes + cryptographically
  // verifies a live tap's SDM proof, and there is nothing to verify here (the tags are already
  // known from our own DB). See `CreatePackageCertificatesUseCase` (API) for the full rationale.
  // Single mode only (VRG-233 AC6) — see the certMode guard below.
  useEffect(() => {
    if (!open || certMode !== 'single') return
    const unsub = subscribeScan(scannedUrl => {
      if (packageScanResolvingRef.current) return
      const match = PACKAGE_HANDLE_URL_RE.exec(scannedUrl)
      if (!match) return
      const packageId = match[1]
      packageScanResolvingRef.current = true
      setPackageScanStatus({ loading: true, message: 'Reading package…', severity: 'info' })
      ;(async () => {
        try {
          // A product is required — the session encodes the added tags under it. Prompt if not set.
          if (!productRef.current?.id) {
            setPackageScanStatus({
              loading: false,
              message: 'Select a product above, then scan the package label again.',
              severity: 'error',
            })
            return
          }

          // Resolve the package for its name (also validates the scanned handle). Company-scoped:
          // a company user only reaches their own company's packages. `api.get` resolves on 4xx.
          const pkgResponse = await api.get(`/packages/${packageId}`)
          const pkgStatus = pkgResponse?.status
          const pkg = pkgResponse?.data
          if (!pkg?.id || (typeof pkgStatus === 'number' && (pkgStatus < 200 || pkgStatus >= 300))) {
            setPackageScanStatus({
              loading: false,
              message: "That package label isn't recognized",
              severity: 'error',
            })
            return
          }

          setPackageScanStatus({
            loading: true,
            message: `Adding tags from ${pkg.name}…`,
            severity: 'info',
          })

          // Add EVERY tag in the package to the scan session (as if each were scanned). The SSE
          // stream ticks the "Tags scanned" counter + running list up; the operator reviews and
          // hits Create Certificates as usual — no certificates are created here.
          const result = await scanSessionRef.current?.addPackage(packageId)
          const added = result?.added ?? 0
          const existing = result?.existing ?? 0
          const total = result?.total ?? added + existing

          // VERA-455 — the box has not been received into inventory, so NOTHING was added and
          // `ScanSessionBlock` has raised the receipt prompt. The tally below must not run:
          // `total` is the box's real tag count, so it would have announced "Added N tags from
          // <box> to the session" for a session that gained none — the exact class of lie the
          // rest of this handler's reporting was written to stop telling.
          //
          // Nothing is said here at all. The prompt is on screen stating the consequence, and
          // the server's own explanatory sentence has already arrived on the SSE as the scan
          // feedback line behind it. A third sentence from this component would be the drift
          // `PACKAGE_TAG_REJECTION_PHRASE` exists to prevent.
          if (result?.receipt?.packageId) {
            setPackageScanStatus({ loading: false, message: null, severity: 'info' })
            return
          }

          if (total === 0) {
            setPackageScanStatus({ loading: false, message: `${pkg.name} has no tags yet`, severity: 'error' })
            return
          }

          const parts = [`Added ${total} tag${total === 1 ? '' : 's'} from ${pkg.name} to the session`]
          if (existing > 0) {
            parts.push(`${existing} already ${existing === 1 ? 'has' : 'have'} a certificate`)
          }
          setPackageScanStatus({ loading: false, message: parts.join(' · '), severity: 'success' })
          // AUTO-DISMISS, success only.
          //
          // The banner reports a COMPLETED action whose result is already on screen — the tags
          // it added are listed right above it, with a per-row outcome glyph each. So once it
          // has been read it is a stale receipt, and it was sitting there through every
          // subsequent scan, still naming the previous box. An operator running a stack of
          // boxes had a permanent "Added 2 tags from …" under a list that had moved on.
          //
          // Errors are NOT auto-dismissed: they describe something that did not happen and has
          // no on-screen consequence to point at, so they stay until the next scan replaces
          // them or the modal closes.
          //
          // Guarded on the message it set, so a scan that lands during the timeout clears its
          // own banner rather than having this one wipe it.
          const settled = parts.join(' · ')
          window.setTimeout(() => {
            setPackageScanStatus(prev =>
              prev.message === settled ? { loading: false, message: null, severity: 'info' } : prev,
            )
          }, 6000)
        } catch (err) {
          setPackageScanStatus({ loading: false, message: getErrorMessage(err), severity: 'error' })
        } finally {
          packageScanResolvingRef.current = false
        }
      })()
    })
    return () => {
      unsub()
      packageScanResolvingRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, certMode, subscribeScan, notification, refetch])

  return (
    <Modal
      title="Create certificate"
      open={open}
      onClose={handleClose}
      actions={
        activeStep === 0 ? (
          <>
            <Button
              disableElevation
              variant="secondary"
              onClick={handleClose}
            >
              Cancel
            </Button>
            {/* Tooltip on a disabled Button needs a wrapper element (MUI cannot attach
                listeners to a disabled child) — a bare span, not another interactive
                element, since the button itself stays the only real control. */}
            <Tooltip title={canProceedToScanning ? '' : nextDisabledReason}>
              <span>
                <Button
                  disableElevation
                  onClick={() => setActiveStep(1)}
                  disabled={!canProceedToScanning}
                >
                  Next
                </Button>
              </span>
            </Tooltip>
          </>
        ) : (
          // Back sits at the far left (separate from the Cancel/confirm group on the
          // right), matching InstallationHandleModal's established wizard footer.
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
            <Box>
              <Button
                disableElevation
                variant="tertiary"
                startIcon={<ChevronLeft size={16} />}
                onClick={() => setActiveStep(0)}
                disabled={isCreatingCertificates || isCreatingGroup}
              >
                Back
              </Button>
            </Box>
            <Stack direction="row" alignItems="center" spacing={3}>
              <Button
                disableElevation
                variant="secondary"
                onClick={handleClose}
                disabled={isCreatingCertificates || isCreatingGroup}
              >
                Cancel
              </Button>
              {certMode === 'single' ? (
                // VRG-512 (§46) — same "disabled needs a reason" treatment as Next above.
                // The empty-state copy just above this bar already SAYS "Start scanning
                // tags…", but that copy scrolls out of view once there's a running list of
                // scanned tags, and the reason for a disabled Create Certificates button
                // should not depend on the user having read something above the fold.
                <Tooltip title={pendingCount === 0 && !isCreatingCertificates ? 'Scan at least one tag to create a certificate' : ''}>
                  <span>
                    <Button
                      data-tour="create-certificates-submit"
                      disableElevation
                      onClick={handleCreateCertificates}
                      disabled={isCreatingCertificates || pendingCount === 0}
                      loading={isCreatingCertificates}
                    >
                      Create Certificates
                    </Button>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title={!isCreatingGroup && currentGroupCount < 2 ? 'Scan at least 2 tags for this group to continue' : ''}>
                  <span>
                    <Button
                      disableElevation
                      onClick={handleCreateGroups}
                      disabled={isCreatingGroup || currentGroupCount < 2}
                      loading={isCreatingGroup}
                    >
                      Create certificate group
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Stack>
          </Stack>
        )
      }
    >
      <Stack spacing={4}>
        {/* VRG-231 review item 8 — the stepper (Stepper steps={WIZARD_STEPS} …) was removed
            per user directive; it's still a 2-step wizard (activeStep 0/1, Back/Next), just
            without the visual step indicator. The three create-certificates-* data-tour
            anchors below are unaffected — none of them lived on the stepper itself. */}
        {activeStep === 0 && (
          <>
            {product?.id ? (
              // Preselected from a product page → the product is implied, so no card
              // (straight to the scan session). Picked in-modal → the card IS the
              // selection, so show it with "Change".
              !isPreselected && (
                <>
                  <Typography variant="body2">Selected product</Typography>
                  {/* "Change" (shared Preview onChange) clears the pick and reopens the
                      search — disabled once tags are scanned (locked). */}
                  <ProductPreview product={product} disabled={locked} onChange={handleClearProduct} />
                  {locked && (
                    <Typography variant="body3" color="text.secondary">
                      Scanned tags are tied to this product. Clear them to choose another.
                    </Typography>
                  )}
                </>
              )
            ) : (
              <Stack direction="column" gap={3}>
                {/* Shared search picker (same pattern as Set-default-tag): typing opens
                    a constrained popover below with ProductPreview rows. */}
                <Box data-tour="create-certificates-product-picker">
                  <SearchFilter
                    notFilter
                    singleSelect
                    column={{ headerName: 'Select a product', field: 'product' } as any}
                    rows={products as any}
                    filter={[] as any}
                    searchParams={{ fields: ['title', 'sku'], preview: 'products' }}
                    onChange={((item: any) => setProduct(item)) as any}
                  />
                </Box>
                {isLoadingProducts && (
                  <Typography variant="body3" color="text.secondary">
                    Loading products…
                  </Typography>
                )}
                {!isLoadingProducts && products.length === 0 && (
                  <Typography variant="body3" color="text.secondary">
                    No products found. Create a product first to issue certificates.
                  </Typography>
                )}
              </Stack>
            )}

            {product?.id && (
              <>
                <Divider />

                {/* Single — a plain bordered radio row, no expansion (mirrors the
                    scan-method rows below it).

                    VRG-512 — MUI's `<Radio>` bakes in ~9px of its own padding (a 24px SVG
                    icon centered inside a 42px hit box), while the "Scan sources" rows
                    below render a bare 20x20 `SourceStatus` Box with NO padding of its
                    own. Both rows' outer bordered containers already share the identical
                    left edge (`px: 4`) — measured equal before this change — so the
                    container structure was never the problem. The Radio's own built-in
                    chrome was pushing its painted circle ~9-11px right of the dot below
                    it. `padding: 0` removes that chrome so the drawn shape sits flush
                    against the row's `spacing`, the same rule the dot already follows.
                    That shrinks the radio's own hit target from 42x42 to 24x24, so the
                    row itself becomes the click target (role="button", mirroring the
                    "Scan with…" rows immediately below) rather than relying on hitting
                    the now-smaller circle precisely. */}
                <Stack
                  role="button"
                  tabIndex={locked ? -1 : 0}
                  aria-pressed={certMode === 'single'}
                  onClick={() => !locked && setCertMode('single')}
                  onKeyDown={e => {
                    if (locked) return
                    if (e.target !== e.currentTarget) return
                    if (e.key !== 'Enter' && e.key !== ' ') return
                    e.preventDefault()
                    setCertMode('single')
                  }}
                  sx={{
                    py: 1,
                    px: 4,
                    border: `1px solid ${theme.palette.primary[100]}`,
                    borderRadius: 2,
                    cursor: locked ? 'default' : 'pointer',
                    outline: 'none',
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Radio
                      checked={certMode === 'single'}
                      onChange={() => setCertMode('single')}
                      disabled={locked}
                      value="cert-mode-single"
                      name="cert-mode"
                      inputProps={{ 'aria-label': 'Single' }}
                      sx={{ p: 0, '&.Mui-checked': { color: theme.palette.grey[900] } }}
                    />
                    <Typography variant="body2">Single</Typography>
                    <Tooltip title="One certificate per scanned tag.">
                      <InfoIcon strokeWidth={1} size={18} color={theme.palette.grey[800]} />
                    </Tooltip>
                  </Stack>
                </Stack>

                {/* Group — same bordered-radio-row idiom, but expands (like the phone/
                    reader scan-method rows) to reveal the Divisible/Indivisible cards
                    while selected. Same VRG-512 alignment fix as Single, above. */}
                <Stack
                  role="button"
                  tabIndex={locked ? -1 : 0}
                  aria-pressed={certMode === 'group'}
                  onClick={() => !locked && setCertMode('group')}
                  onKeyDown={e => {
                    if (locked) return
                    if (e.target !== e.currentTarget) return
                    if (e.key !== 'Enter' && e.key !== ' ') return
                    e.preventDefault()
                    setCertMode('group')
                  }}
                  sx={{
                    py: 1,
                    px: 4,
                    pb: certMode === 'group' ? 4 : 1,
                    border: `1px solid ${theme.palette.primary[100]}`,
                    borderRadius: 2,
                    transition: 'all ease-in-out 200ms',
                    cursor: locked ? 'default' : 'pointer',
                    outline: 'none',
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Radio
                      checked={certMode === 'group'}
                      onChange={() => setCertMode('group')}
                      disabled={locked}
                      value="cert-mode-group"
                      name="cert-mode"
                      inputProps={{ 'aria-label': 'Group' }}
                      sx={{ p: 0, '&.Mui-checked': { color: theme.palette.grey[900] } }}
                    />
                    <Typography variant="body2">Group</Typography>
                    <Tooltip title="One tagless parent certificate plus a child certificate for every tag you scan.">
                      <InfoIcon strokeWidth={1} size={18} color={theme.palette.grey[800]} />
                    </Tooltip>
                  </Stack>

                  <Collapse in={certMode === 'group'}>
                    {/* Stop the collapse panel's own controls (the divisibility cards)
                        from bubbling a click up into this row's onClick — matches the
                        `stopRowToggle` guard ScanSessionBlock uses for its expanded
                        panels below. */}
                    <Box sx={{ pt: 3 }} onClick={e => e.stopPropagation()}>
                      <DivisibilityOptionPicker value={divisibility} onChange={setDivisibility} disabled={locked} />
                    </Box>
                  </Collapse>
                </Stack>
              </>
            )}
          </>
        )}

        {/* ScanSessionBlock stays mounted across both steps once a product is picked, so
            the underlying scan session (SSE stream, phone/reader connection) survives the
            step change — only which of its sub-sections are visible changes per step. */}
        {product && (
          <Box data-tour="create-certificates-scan-session">
            <ScanSessionBlock
              ref={scanSessionRef}
              onScanTags={setScannedTagIds}
              onGroupableTagsChange={setGroupableTagIds}
              onConnectionChange={setScanDeviceConnected}
              productId={product.id}
              // STATED, not inferred (the VRG-265 rule the API's own use-case follows). Omitting it
              // still opens a certificate session server-side, since that is the API's default, but
              // the BLOCK will not guess: an unstated purpose gets the neutral "Scanned tags" label
              // rather than wording borrowed from whichever caller was written first.
              purpose="certificate"
              onPendingCountChange={setPendingCount}
              // Reader option is real now (Vera Reader Helper → PC/SC device); offer both
              // the phone QR and the USB reader as scan methods.
              showEncoderOption={true}
              showMethodSelector={activeStep === 0}
              showScans={activeStep === 1}
              groupLabel={certMode === 'group' ? `Group ${groupIndex}` : undefined}
              groupCount={certMode === 'group' ? currentGroupCount : undefined}
              scanHint={
                certMode === 'group' && activeStep === 1 ? (
                  <Button
                    variant="tertiary"
                    size="small"
                    startIcon={<PlusIcon strokeWidth={1} size={16} />}
                    disabled={currentGroupCount < 2}
                    onClick={handleAddNextGroup}
                  >
                    Add Next Group
                  </Button>
                ) : undefined
              }
              // The operator ended the run from the phone in their hand. Close, don't just note
              // it: the session behind this modal is gone, so the QR is stale and nothing further
              // can be scanned into it — leaving it open would present a live-looking surface
              // that silently accepts nothing. Any tags already pending die with the session,
              // which is what "end session" means; creating from them is the OTHER button.
              onSessionEnded={handleClose}
            />
          </Box>
        )}

        {/* VERA-790 Phase 3 "package handle" — the same session takes either a tag (adds one) or a
            package label (adds every tag in that package). Only meaningful once a product is
            selected (the session needs one). Sits just above the modal's bottom bar; a package
            scan's status shows below it.

            IT NO LONGER GATES ON `readerConnected`, and the copy no longer says "on the reader".
            Both were wrong: the PHONE can scan a package label too — `admins.admin.controller.ts`
            `mobile-scan` matches `/pkg/<uuid>` and calls `addPackageTagsToSession`, the same
            expansion the desk reader triggers. So the old gate hid a working capability from every
            phone-only operator, and the old sentence named the one source they were not using.

            The `certMode === 'single'` and `activeStep === 1` guards are VRG-233's, and they
            stay: a package's tags are independent items, which conflicts with a group's "N
            tagged components of ONE item" model (AC6), and on step 0 there is no scan surface
            for a package row to sit above. */}
        {certMode === 'single' && activeStep === 1 && product && (
          <Stack
            data-tour="create-certificates-package-handle"
            direction="row"
            spacing={3}
            alignItems="center"
            sx={{
              px: 4,
              py: 3,
              border: `1px solid ${theme.palette.primary[100]}`,
              borderRadius: 2,
            }}
          >
            <PackageIcon
              strokeWidth={1}
              size={20}
              color={theme.palette.text.secondary}
              style={{ flexShrink: 0 }}
            />
            <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              {/* "Scan", one verb: tapping a phone and presenting to the reader are the same
                  gesture, and "scan" is the word the glossary already defines for it. "Package
                  label" because that is what this flow calls the object everywhere else,
                  including this modal's own error ("That package label isn't recognized") — 8
                  call sites to "package tag"'s 2. */}
              Or scan a package label to add all of its tags at once.
            </Typography>
          </Stack>
        )}
        {certMode === 'single' && packageScanStatus.message && (
          <Alert severity={packageScanStatus.severity} description={packageScanStatus.message} />
        )}
      </Stack>
    </Modal>
  )
}
